import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {DimensionUpdatedService} from "app/services/domain/dimensions/dimension-updated";
import {DimensionDefinitionService} from "app/services/domain/dimensions/dimension-definition.service";
import * as Models from "app/services/proxy/model/models";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {Subject} from "rxjs";

@Injectable()
export class DimensionDefinitionVersionService extends SharedSvc.BaseDefinitionVersionService<Models.DimensionDefinitionConfiguration, Models.DimensionDefinitionVersion, Models.DimensionDefinition, DimensionVersionExtended>
{
    private readonly dimensionUpdated: Subject<void>;

    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService,
                updatedSvc: DimensionUpdatedService)
    {
        super(api, errors, cache, Models.DimensionDefinitionVersion, DimensionVersionExtended.newInstance);
        this.dimensionUpdated = updatedSvc.updated;
    }

    protected cachePrefix(): string
    {
        return "DimensionDefinitionVersion";
    }

    public async createVersion(model: Models.DimensionDefinitionVersion): Promise<Models.DimensionDefinitionVersion>
    {
        const version = await this.domain.apis.dimensionDefinitionVersions.createDimensionDefinitionVersion(model);
        this.dimensionUpdated.next();
        return version;
    }

    protected getRaw(versionId: string): Promise<Models.DimensionDefinitionVersion>
    {
        return this.domain.apis.dimensionDefinitionVersions.getDimensionDefinitionVersion(versionId);
    }

    protected canBatch(): boolean
    {
        return true;
    }

    protected async getBatchRaw(ids: string[]): Promise<Models.DimensionDefinitionVersion[]>
    {
        const batch = await this.domain.apis.dimensionDefinitionVersions.batchDimensionBatchDefinitionVersion(Models.BatchRequest.newInstance({
                                                                                                                                                  ids: ids
                                                                                                                                              }));

        return Object.values(batch.results);
    }

    public async makeHead(versionId: string): Promise<DimensionVersionExtended>
    {
        const version = await this.domain.apis.dimensionDefinitionVersions.makeDimensionDefinitionVersionHead(versionId);
        this.dimensionUpdated.next();
        return this.wrapModel(version);
    }

    public async makeRelease(versionId: string): Promise<DimensionVersionExtended>
    {
        const version = await this.domain.apis.dimensionDefinitionVersions.makeDimensionDefinitionVersionRelease(versionId);
        this.dimensionUpdated.next();
        return this.wrapModel(version);
    }
}

export class DimensionVersionExtended extends SharedSvc.ExtendedDefinitionVersionModel<Models.DimensionDefinitionConfiguration, Models.DimensionDefinitionVersion, Models.DimensionDefinition>
{
    protected definitionSvc(): DimensionDefinitionService
    {
        return this.domain.dimensionDefinition;
    }

    protected definitionVersionSvc(): DimensionDefinitionVersionService
    {
        return this.domain.dimensionDefinitionVersion;
    }

    public static newInstance(svc: DimensionDefinitionVersionService,
                              model: Models.DimensionDefinitionVersion): DimensionVersionExtended
    {
        return new DimensionVersionExtended(svc, model, "DimensionDefinitionVersion");
    }
}
